import React, { useState } from "react";
import { Button, Form, Input as AntInput, notification, Typography, Alert } from "antd";
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { resendVerificationEmail } from "../../../service/authService";
import styled from "styled-components";
import { AuthLayout } from "../auth-layout/AuthLayout";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Title, Text, Link } = Typography;

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 12px !important;
    color: #FEFFFC !important;
    font-weight: 500;
    text-align: center;
    font-size: clamp(2.5rem, 5vw, 4rem) !important;
  }
`;

const StyledSubtitle = styled(Text)`
  display: block;
  margin-bottom: 48px;
  color: #FEFFFC;
  font-size: 18px;
  text-align: center;
  opacity: 0.9;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 32px;
  color: #FEFFFC !important;
  font-weight: 500;
  opacity: 0.8;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    transform: translateX(-4px);
  }

  .anticon {
    margin-right: 8px;
  }
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 32px;

  .ant-form-item-label > label {
    color: #FEFFFC !important;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-input {
    height: 52px;
    background-color: #263e44 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 8px !important;
    color: #FEFFFC !important;
    padding: 0 12px !important;
    transition: all 0.3s ease;

    &:hover, &:focus {
      border-color: #EBFED1 !important;
      background-color: #2c474d !important;
      box-shadow: none !important;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    color: #ff4d4f;
  }
`;

const StyledButton = styled(Button)``;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-top: 40px;
  display: flex;
  align-items: flex-start;
  color: #FEFFFC;

  .anticon {
    font-size: 20px;
    color: #EBFED1;
    margin-right: 16px;
    margin-top: 2px;
  }

  .info-content {
    flex: 1;

    .info-title {
      font-weight: 600;
      margin-bottom: 8px;
      display: block;
    }

    .info-text {
      font-size: 14px;
      opacity: 0.8;
      line-height: 1.6;
      margin: 0;
    }
  }
`;

const SuccessCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #FEFFFC;

  .success-icon {
    font-size: 64px;
    color: #EBFED1;
    margin-bottom: 24px;
  }

  .success-title {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .success-description {
    font-size: 16px;
    opacity: 0.9;
    margin-bottom: 32px;
    line-height: 1.6;
  }
`;

export const ResendVerificationEmailNew: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const { mutateAsync, isLoading } = useMutation(resendVerificationEmail, {
    onSuccess: () => {
      setEmailSent(true);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || t("resendVerificationEmail.sendErrorFallback");

      notification.error({
        message: t("resendVerificationEmail.errorTitle"),
        description: errorMessage,
        placement: "bottomRight",
      });
    },
  });

  const handleSubmit = async (values: any) => {
    await mutateAsync(values.email);
  };

  if (emailSent) {
    return (
      <AuthLayout maxWidth={500}>
        <SuccessCard>
          <CheckCircleOutlined className="success-icon" />
          <div className="success-title">{t("resendVerificationEmail.successTitle")}</div>
          <div className="success-description">
            {t("resendVerificationEmail.successInstruction")}
            <br />
            <br />
            <strong>{t("resendVerificationEmail.successCheckInbox")}</strong> {t("resendVerificationEmail.successCheckSpam")}
            <br />
          </div>
          <StyledButton block onClick={() => navigate("/login")}>
            {t("resendVerificationEmail.backToLogin")}
          </StyledButton>
        </SuccessCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout maxWidth={540}>
      <BackLink onClick={() => navigate("/login")}>
        <ArrowLeftOutlined />
        {t("resendVerificationEmail.backLink")}
      </BackLink>

      <StyledTitle level={1}>
        {t("resendVerificationEmail.title")}
      </StyledTitle>
      <StyledSubtitle>
        {t("resendVerificationEmail.subtitle")}
      </StyledSubtitle>

      <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
        <StyledFormItem
          name="email"
          label={t("resendVerificationEmail.emailLabel")}
          rules={[
            { required: true, message: t("resendVerificationEmail.emailRequired") },
            {
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("resendVerificationEmail.emailInvalid")
            },
          ]}
          normalize={(value) => value?.trim().toLowerCase()}
        >
          <AntInput
            placeholder={t("resendVerificationEmail.emailPlaceholder")}
            size="large"
            autoComplete="email"
            autoFocus
          />
        </StyledFormItem>

        <Form.Item style={{ marginBottom: 0 }}>
          <StyledButton type="primary" htmlType="submit" loading={isLoading} block>
            {isLoading ? t("resendVerificationEmail.submitLoading") : t("resendVerificationEmail.submit")}
          </StyledButton>
        </Form.Item>
      </Form>

      <InfoBox>
        <MailOutlined />
        <div className="info-content">
          <span className="info-title">{t("resendVerificationEmail.infoTitle")}</span>
          <p className="info-text">
            {t("resendVerificationEmail.infoText")}
          </p>
        </div>
      </InfoBox>
    </AuthLayout>
  );
};
